import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
  modify,
  superFetch,
} from '@pflegenavi/shared-frontend/platform';
import type {
  FamilyMemberPhoenix,
  FamilyMemberResidentRelationship,
  Gender,
  GetAllResidentsResult,
  Resident,
  ResidentState,
  ShowResidentDto,
  UpdateResidentCashPayerStateDto,
} from '@pflegenavi/shared/api';
import { Filter } from '../filter';

export interface MandateValidationResponse {
  data: {
    valid: boolean;
    errors?: Array<{ field: string; message: string }>;
  };
}

export interface MandateCreateResponse {
  data: {
    recipient: Record<string, unknown>;
    mandate: {
      id: string;
      status: string;
      redirect_url: string;
    };
    currency: string;
    country: string;
  };
}

export interface CreateOfflineMandateBody {
  recipient_data: Record<string, unknown>;
  confirmation_checked_at: string;
  country?: string;
  sepa_document_image_ids?: string[];
}

// Maps each stored SEPA document image id to a presigned download URL.
export type MandateDocumentsResponse = Record<string, string>;

export interface MandateDeleteResponse {
  data: Record<string, unknown>;
}

export interface MandateRefreshResponse {
  data: {
    mandate_status: string;
  };
}

export interface MandateDetailsResponse {
  data: {
    account_holder_name: string | null;
    iban_masked: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    city: string | null;
    postal_code: string | null;
    country: string | null;
  };
}

export interface GetAllResidentsPhoenixParams {
  page?: number;
  pageSize?: number;
  states?: ResidentState[];
  nursingHomeId: string;
}

export interface CreateResidentResult {
  data: any;
}

export type InitialBalance =
  | {
      cash_list_id: string;
      notes?: string;
      coins: number[];
    }
  | {
      cash_list_id: string;
      notes?: string;
      bank_amount: number;
    }
  | {
      notes?: string;
      amount: number;
    };

export interface UpdateResidentFamilyMemberDto {
  user_id: string;
  first_name: string;
  last_name: string;
  notes?: string;
  phone?: string;
  address?: string;
  zip_code?: string;
  city?: string;
  country?: string;
  relationship_to_resident: FamilyMemberResidentRelationship;
}

export interface CreateResidentParams {
  body: {
    nursing_home_id: string;
    data: {
      entry_date: Date;
      firstname: string;
      lastname: string;
      gender: Gender;
      birthdate?: Date;
      resident_accounting_id?: string;
      initial_balance?: InitialBalance;
      service_providers?: Array<{
        global_service_provider_id: string;
      }>;
      family_member?: {
        first_name: string;
        last_name: string;
        email: string;
        notes?: string;
        phone?: string;
        address?: string;
        zip_code?: string;
        city?: string;
        country?: string;
        relationship_to_resident: FamilyMemberResidentRelationship | undefined;
      };
    };
  };
}
export interface PaymentScheduleResponse {
  data: {
    payment_initiation: string | null;
    payment_schedule: string | null;
    payment_schedule_day: number | null;
    payment_schedule_max_amount: number | null;
    payment_schedule_payin_amount: number | null;
    payment_schedule_max_wallet_amount: number | null;
    payment_disabled_date: string | null;
  };
}

export interface IResidentPhoenixApi extends Api {
  getAllResidents(dataIn: {
    params: GetAllResidentsPhoenixParams;
  }): Promise<GetAllResidentsResult>;

  createResident(dataIn: CreateResidentParams): Promise<CreateResidentResult>;
  showResident(dataIn: {
    params: {
      residentId: string;
    };
  }): Promise<ShowResidentDto>;
  showResidentForFamilyMember(dataIn: {
    params: { tenant: string; residentId: string };
  }): Promise<ShowResidentDto>;
  resendFamilyMemberWelcomeEmail(dataIn: {
    params: {
      residentId: string;
      familyMemberId: string;
    };
  }): Promise<void>;

  addNewFamilyMember(dataIn: {
    params: {
      residentId: string;
    };
    body: {
      data: FamilyMemberPhoenix;
    };
  }): Promise<{
    status: 'Created';
  }>;

  deleteFamilyMemberForGivenResidentPhoenix(dataIn: {
    params: {
      residentId: string;
      familyMemberId: string;
    };
  }): Promise<{
    success: boolean;
  }>;

  updateFamilyMemberForGivenResidentPhoenix(dataIn: {
    params: {
      residentId: string;
      familyMemberId: string;
    };
    body: {
      data: UpdateResidentFamilyMemberDto;
    };
  }): Promise<{ data: UpdateResidentFamilyMemberDto }>;

  updateResidentCashPayerStatus(dataIn: {
    params: {
      residentId: string;
    };
    body: {
      data: UpdateResidentCashPayerStateDto;
    };
  }): Promise<{ data: { resident: Resident } }>;

  validateOfflineMandate(dataIn: {
    params: { residentId: string };
    body: { recipient_data: Record<string, unknown>; country?: string };
  }): Promise<MandateValidationResponse>;

  createOfflineMandate(dataIn: {
    params: { residentId: string };
    body: CreateOfflineMandateBody;
  }): Promise<MandateCreateResponse>;

  getMandateDocumentUploadUrl(
    residentId: string,
    imageId: string,
    fileType: string,
    md5Hash: string
  ): Promise<string | undefined>;

  getMandateDocuments(dataIn: {
    params: { residentId: string };
  }): Promise<MandateDocumentsResponse>;

  deleteOfflineMandate(dataIn: {
    params: { residentId: string };
  }): Promise<MandateDeleteResponse>;

  refreshOfflineMandate(dataIn: {
    params: { residentId: string };
  }): Promise<MandateRefreshResponse>;

  getOfflineMandateDetails(dataIn: {
    params: { residentId: string };
  }): Promise<MandateDetailsResponse>;

  getPaymentSchedule(dataIn: {
    params: { residentId: string };
  }): Promise<PaymentScheduleResponse>;

  updatePaymentSchedule(dataIn: {
    params: { residentId: string };
    body: Record<string, unknown>;
  }): Promise<PaymentScheduleResponse>;

  getRecurringItemsForResident(dataIn: {
    params: { residentId: string };
  }): Promise<ResidentRecurringItemsPhoenixResponse>;
}

export interface ResidentRecurringItemPhoenix {
  id: string;
  name: string;
  amount: number;
  amount_in_cents: number;
  nursing_home_id: string;
  cadence: string;
  start_date: string;
  end_date: string | null;
  state: string;
  charge_full_period: boolean;
  compute_daily: boolean;
  auto_add_residents: boolean;
  generate_receipts: boolean;
  earnings: boolean;
  tax_code: {
    id: string;
    country_id: string;
    tax_name: string;
    tax_abbreviation: string;
    tax_amount: number;
  } | null;
}

export interface ResidentRecurringItemsPhoenixResponse {
  data: ResidentRecurringItemPhoenix[];
}

export class ResidentPhoenixApi implements IResidentPhoenixApi {
  baseUrl: string;

  getAllResidents: IResidentPhoenixApi['getAllResidents'];
  createResident: IResidentPhoenixApi['createResident'];
  showResident: IResidentPhoenixApi['showResident'];
  showResidentForFamilyMember: IResidentPhoenixApi['showResidentForFamilyMember'];
  resendFamilyMemberWelcomeEmail: IResidentPhoenixApi['resendFamilyMemberWelcomeEmail'];
  public addNewFamilyMember: IResidentPhoenixApi['addNewFamilyMember'];
  public deleteFamilyMemberForGivenResidentPhoenix: IResidentPhoenixApi['deleteFamilyMemberForGivenResidentPhoenix'];
  public updateFamilyMemberForGivenResidentPhoenix: IResidentPhoenixApi['updateFamilyMemberForGivenResidentPhoenix'];
  public updateResidentCashPayerStatus: IResidentPhoenixApi['updateResidentCashPayerStatus'];
  public validateOfflineMandate: IResidentPhoenixApi['validateOfflineMandate'];
  public createOfflineMandate: IResidentPhoenixApi['createOfflineMandate'];
  public deleteOfflineMandate: IResidentPhoenixApi['deleteOfflineMandate'];
  public refreshOfflineMandate: IResidentPhoenixApi['refreshOfflineMandate'];
  public getOfflineMandateDetails: IResidentPhoenixApi['getOfflineMandateDetails'];
  public getMandateDocuments: IResidentPhoenixApi['getMandateDocuments'];
  public getPaymentSchedule: IResidentPhoenixApi['getPaymentSchedule'];
  public updatePaymentSchedule: IResidentPhoenixApi['updatePaymentSchedule'];
  public getRecurringItemsForResident: IResidentPhoenixApi['getRecurringItemsForResident'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);
    const familyMemberBaseUrl = getPhoenixApiTenantUrl('family-member', apiUrl);

    this.getAllResidents = get({
      authContext,
      url: (params) => {
        const filter = new Filter();
        // Bare `if` (no length check) is intentional: an explicit `[]`
        // still sends the (valueless) `in` filter, matching zero rows,
        // rather than being treated as absent like `fieldIfPresent` would.
        if (params.states) {
          filter.field('state', 'in', params.states);
        }
        const query = filter.build();

        query.append('nursing_home_id', params.nursingHomeId);

        if (params.page) {
          query.append('page', params.page.toString());
        }

        if (params.pageSize) {
          query.append('page_size', params.pageSize.toString());
        }

        return `${this.baseUrl}/resident?${query.toString()}`;
      },
    });

    this.createResident = modify({
      authContext,
      url: `${this.baseUrl}/resident`,
      method: 'POST',
    });

    this.showResident = get({
      authContext,
      url: (params) => `${this.baseUrl}/resident/${params.residentId}`,
    });

    this.showResidentForFamilyMember = get({
      authContext,
      url: (params) =>
        `${familyMemberBaseUrl}/${params.tenant}/${params.residentId}/resident-info`,
    });

    this.resendFamilyMemberWelcomeEmail = modify({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/family-member/${params.familyMemberId}/resend_welcome`,
      method: 'POST',
    });

    this.addNewFamilyMember = modify({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/family-member`,
      method: 'POST',
    });

    this.deleteFamilyMemberForGivenResidentPhoenix = modify<
      undefined,
      {
        success: boolean;
      },
      {
        success: boolean;
      },
      {
        residentId: string;
        familyMemberId: string;
      }
    >({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/family-member/${params.familyMemberId}`,
      method: 'DELETE',
    });

    this.updateFamilyMemberForGivenResidentPhoenix = modify({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/family-member/${params.familyMemberId}`,
      method: 'PUT',
    });

    this.updateResidentCashPayerStatus = modify({
      authContext,
      url: (params) => `${this.baseUrl}/resident/${params.residentId}`,
      method: 'PATCH',
      transformBody: (data: { data: UpdateResidentCashPayerStateDto }) => ({
        data: {
          cash_payer: data.data.cashPayer,
        },
      }),
    });

    this.validateOfflineMandate = modify({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/mandate/validate`,
      method: 'POST',
    });

    this.createOfflineMandate = modify({
      authContext,
      url: (params) => `${this.baseUrl}/resident/${params.residentId}/mandate`,
      method: 'POST',
    });

    this.deleteOfflineMandate = modify({
      authContext,
      url: (params) => `${this.baseUrl}/resident/${params.residentId}/mandate`,
      method: 'DELETE',
    });

    this.refreshOfflineMandate = modify({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/mandate/refresh`,
      method: 'POST',
    });

    this.getOfflineMandateDetails = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/mandate/details`,
    });

    this.getMandateDocuments = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/mandate/documents`,
    });

    this.getPaymentSchedule = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/payment-schedule`,
    });

    this.updatePaymentSchedule = modify({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/payment-schedule`,
      method: 'PUT',
    });

    this.getRecurringItemsForResident = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/resident/${params.residentId}/recurring-items`,
    });
  }

  /**
   * Requests a presigned S3 PUT url for a hand-written SEPA document image.
   * The image id is generated client-side and later passed to
   * `createOfflineMandate` as part of `sepa_document_image_ids`.
   *
   * @example
   * api.getMandateDocumentUploadUrl(residentId, uuid, 'image/jpeg', md5Hash)
   */
  async getMandateDocumentUploadUrl(
    residentId: string,
    imageId: string,
    fileType: string,
    md5Hash: string
  ): Promise<string | undefined> {
    // URLSearchParams, not interpolation: the md5 is base64, so an unescaped
    // `+` would reach the server as a space.
    const query = new URLSearchParams({ fileType, md5Hash });
    const result = await superFetch(
      this.authContext,
      `${
        this.baseUrl
      }/resident/${residentId}/mandate/documents/${imageId}?${query.toString()}`,
      {
        method: 'POST',
        headers: new Headers({ 'content-type': 'application/json' }),
      }
    );
    return result.text();
  }
}
